import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { CourtCase, CourtCaseSummary, Division, CaseStatus, Page, PartyRole } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const DIVISIONS: Division[] = ['SUPREME_CIVIL', 'COMMERCIAL', 'FAMILY', 'MAGISTRATES', 'EMPLOYMENT_TRIBUNAL', 'APPEALS'];
const CASE_STATUSES: CaseStatus[] = [
  'FILED',
  'SERVED',
  'ISSUE_JOINED',
  'HEARING_SCHEDULED',
  'JUDGMENT_RESERVED',
  'JUDGMENT_DELIVERED',
  'CLOSED',
  'DISCONTINUED',
];
const PARTY_ROLES: PartyRole[] = ['PLAINTIFF', 'DEFENDANT', 'PETITIONER', 'RESPONDENT', 'APPLICANT', 'ACCUSED', 'THIRD_PARTY', 'WITNESS'];
const OFFICER_ROLES = ['COURT_CLERK', 'REGISTRAR_COURT', 'JUDGE', 'TRIBUNAL_MEMBER'];

/** GET /api/v1/judiciary/cases — officer-only registry list. */
export function CaseRegister() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.some((r) => OFFICER_ROLES.includes(r)) ?? false;
  const navigate = useNavigate();
  const [division, setDivision] = useState<Division | ''>('');
  const [status, setStatus] = useState<CaseStatus | ''>('');
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const params = new URLSearchParams({ limit: '50' });
  if (division) params.set('division', division);
  if (status) params.set('status', status);
  if (q.trim()) params.set('q', q.trim());
  const listQ = useApiQuery<Page<CourtCaseSummary>>(isOfficer ? `/api/v1/judiciary/cases?${params.toString()}` : null);

  return (
    <div>
      <PageHeader
        title="Case Register"
        subtitle="Every case e-filed with the Judiciary, across all 6 divisions."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New case (e-filing)
          </button>
        }
      />

      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Case number contains" className="min-w-[220px] flex-1">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. CC-2026…" />
        </Field>
        <Field label="Division" className="!mb-0">
          <select className="field-input !w-auto" value={division} onChange={(e) => setDivision(e.target.value as Division | '')}>
            <option value="">All</option>
            {DIVISIONS.map((d) => (
              <option key={d} value={d}>
                {d.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Status" className="!mb-0">
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as CaseStatus | '')}>
            <option value="">All</option>
            {CASE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No cases match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Case number</th>
                <th className="table-head">Division</th>
                <th className="table-head">Status</th>
                <th className="table-head">Nature of claim</th>
                <th className="table-head">Filed</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/judiciary/cases/${c.id}`)}>
                  <td className="table-cell font-medium">{c.caseNumber}</td>
                  <td className="table-cell text-xs">{c.division.replaceAll('_', ' ')}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="table-cell max-w-xs truncate text-xs text-slate-600">{c.natureOfClaim}</td>
                  <td className="table-cell text-xs">{formatDate(c.filedAt)}</td>
                  <td className="table-cell text-right text-xs">{c.sealed ? '🔒 sealed' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateCaseModal
          onClose={() => setShowCreate(false)}
          onDone={(created) => {
            setShowCreate(false);
            navigate(`/judiciary/cases/${created.id}`);
          }}
        />
      ) : null}
    </div>
  );
}

interface PartyRow {
  role: PartyRole;
  nin: string;
  fullName: string;
  isMinor: boolean;
  attorneyNin: string;
}

function CreateCaseModal({ onClose, onDone }: { onClose: () => void; onDone: (c: CourtCase) => void }) {
  const [division, setDivision] = useState<Division>('SUPREME_CIVIL');
  const [natureOfClaim, setNatureOfClaim] = useState('');
  const [sealed, setSealed] = useState(false);
  const [clientRef, setClientRef] = useState('');
  const [parties, setParties] = useState<PartyRow[]>([{ role: 'PLAINTIFF', nin: '', fullName: '', isMinor: false, attorneyNin: '' }]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const addParty = () => setParties((rows) => [...rows, { role: 'DEFENDANT', nin: '', fullName: '', isMinor: false, attorneyNin: '' }]);
  const removeParty = (i: number) => setParties((rows) => rows.filter((_, idx) => idx !== i));
  const updateParty = <K extends keyof PartyRow>(i: number, field: K, value: PartyRow[K]) =>
    setParties((rows) => rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<CourtCase>('/api/v1/judiciary/cases', {
        division,
        natureOfClaim,
        sealed,
        clientRef: clientRef || undefined,
        parties: parties.map((p) => ({
          role: p.role,
          nin: p.nin || undefined,
          fullName: p.fullName,
          isMinor: p.isMinor,
          attorneyNin: p.attorneyNin || undefined,
        })),
      });
      onDone(created);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to file case');
    } finally {
      setBusy(false);
    }
  };

  const valid = natureOfClaim.trim().length >= 3 && parties.length > 0 && parties.every((p) => p.fullName.trim().length >= 2);

  return (
    <Modal title="New case — e-filing" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Division">
            <select className="field-input" value={division} onChange={(e) => setDivision(e.target.value as Division)}>
              {DIVISIONS.map((d) => (
                <option key={d} value={d}>
                  {d.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Client reference (optional)">
            <input className="field-input" value={clientRef} onChange={(e) => setClientRef(e.target.value)} placeholder="idempotency key for retries" />
          </Field>
        </div>
        <Field label="Nature of claim">
          <input className="field-input" value={natureOfClaim} onChange={(e) => setNatureOfClaim(e.target.value)} placeholder="e.g. Breach of contract — supply agreement" />
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={sealed} onChange={(e) => setSealed(e.target.checked)} />
          Sealed (family / juvenile matter — redacts co-party detail from non-party viewers)
        </label>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="field-label !mb-0">Parties</span>
            <button className="text-xs font-semibold text-brand-700 hover:underline" onClick={addParty}>
              + Add party
            </button>
          </div>
          <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
            {parties.map((p, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-2.5">
                <div className="mb-1.5 grid grid-cols-2 gap-2">
                  <select className="field-input" value={p.role} onChange={(e) => updateParty(i, 'role', e.target.value as PartyRole)}>
                    {PARTY_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r.replaceAll('_', ' ')}
                      </option>
                    ))}
                  </select>
                  <input className="field-input" value={p.fullName} onChange={(e) => updateParty(i, 'fullName', e.target.value)} placeholder="Full name" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input className="field-input" value={p.nin} onChange={(e) => updateParty(i, 'nin', e.target.value)} placeholder="NIN (optional)" />
                  <input
                    className="field-input"
                    value={p.attorneyNin}
                    onChange={(e) => updateParty(i, 'attorneyNin', e.target.value)}
                    placeholder="Attorney NIN (optional)"
                  />
                </div>
                <div className="mt-1.5 flex items-center justify-between">
                  <label className="flex items-center gap-1.5 text-xs text-slate-600">
                    <input type="checkbox" checked={p.isMinor} onChange={(e) => updateParty(i, 'isMinor', e.target.checked)} />
                    Minor
                  </label>
                  {parties.length > 1 ? (
                    <button className="text-xs text-slate-400 hover:text-rose-600" onClick={() => removeParty(i)}>
                      Remove
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !valid}>
            {busy ? 'Filing…' : 'File case'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
